'use client';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useLocale } from 'next-intl';
import { useEffect, useRef } from 'react';
import { QueryApi } from '@/features/query/api';
import { localeToLang } from '@/shared/i18n/localeToLang';

const useSyncPreferredLang = () => {
    const locale = useLocale();
    const customerQuery = useQuery(QueryApi.Customer.queries.Customer);
    const venueQuery = useQuery(QueryApi.Venue.queries.Venue);
    const updateMutation = useMutation(QueryApi.Customer.mutations.Update);

    const previousLocaleRef = useRef<string | null>(null);

    useEffect(() => {
        const customer = customerQuery.data;
        if (!customer?.id) {
            previousLocaleRef.current = null;
            return;
        }

        const target = localeToLang(locale);
        const venueLanguages = venueQuery.data?.languages ?? [];
        const isFirstSyncForThisCustomer = previousLocaleRef.current === null;
        const localeChanged = previousLocaleRef.current !== locale;
        previousLocaleRef.current = locale;

        if (
            (!isFirstSyncForThisCustomer && !localeChanged) ||
            target === customer.preferredLang ||
            (venueLanguages.length > 0 && !venueLanguages.includes(target)) ||
            updateMutation.isPending ||
            (updateMutation.isError && updateMutation.variables?.preferredLang === target)
        ) {
            return;
        }

        updateMutation.mutate({ preferredLang: target });
    }, [locale, customerQuery.data, venueQuery.data?.languages, updateMutation]);
};

export const PreferredLangSync = () => {
    useSyncPreferredLang();
    return null;
};
